import { useCallback } from 'react';

/**
 * Shared keyboard navigation for autocomplete dropdowns.
 * Handles ArrowDown, ArrowUp, Enter, Escape with consistent behavior.
 *
 * @param {Object} opts
 * @param {Array} opts.suggestions - Current suggestion list
 * @param {boolean} opts.isOpen - Whether dropdown is visible
 * @param {function} opts.onSelect - Called with the selected suggestion on Enter
 * @param {function} opts.onClose - Called to close the dropdown
 * @param {number} opts.highlightedIndex - Currently highlighted index
 * @param {function} opts.setHighlightedIndex - State setter for highlighted index
 * @param {function} [opts.onEnterNoSelection] - Called on Enter when no suggestion is highlighted
 */
export function useAutocompleteKeyboard({
  suggestions,
  isOpen,
  onSelect,
  onClose,
  highlightedIndex,
  setHighlightedIndex,
  onEnterNoSelection,
}) {
  return useCallback((e) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'Escape') onClose();
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          onSelect(suggestions[highlightedIndex]);
        } else if (onEnterNoSelection) {
          onEnterNoSelection();
        } else if (suggestions[0]) {
          onSelect(suggestions[0]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  }, [suggestions, isOpen, onSelect, onClose, highlightedIndex, setHighlightedIndex, onEnterNoSelection]);
}
